import React, { useEffect, useMemo, useRef, useState } from 'react';
import AiTrainerBriefingBanner from './AiTrainerBriefingBanner';
import AiTrainerChallengesRailPanel from './AiTrainerChallengesRailPanel';
import { getChallengePerspective } from '../../utils/challengePerspective';
import { aiTrainerHeadingClassName, aiTrainerMutedTextClassName } from './aiTrainerStyles';

function AccordionSection({ title, count, open, onToggle, children }) {
	return (
		<div className="border-b border-gray-200 dark:border-dark-border last:border-b-0">
			<button
				type="button"
				onClick={onToggle}
				className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-bg transition"
			>
				<span className={`flex-1 text-sm font-semibold ${aiTrainerHeadingClassName}`}>{title}</span>
				{count > 0 && (
					<span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold text-white">
						{count}
					</span>
				)}
				<i
					className={`fa-solid fa-chevron-${open ? 'up' : 'down'} text-xs text-gray-400 dark:text-dark-text-secondary`}
					aria-hidden="true"
				/>
			</button>
			{open && <div className="px-4 pb-4">{children}</div>}
		</div>
	);
}

function RailTab({ active, count, label, onClick }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition ${
				active
					? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
					: 'border-transparent text-gray-500 dark:text-dark-text-secondary hover:text-gray-700 dark:hover:text-dark-text'
			}`}
		>
			{label}
			{count > 0 && (
				<span
					className={`inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold ${
						active ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 dark:bg-dark-bg dark:text-dark-text-secondary'
					}`}
				>
					{count}
				</span>
			)}
		</button>
	);
}

export default function AiTrainerAlertsRail({
	briefing,
	onDismissBriefing,
	onSnooze,
	onRunAction,
	actionLoading = false,
	nudgesSnoozed = false,
	nudgeSnoozedUntil = null,
	challenges = [],
	challengesLoading = false,
	challengesError = '',
	onDayCheck,
	isDayCheckPending,
	challengeFocusNonce = 0
}) {
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('alerts');
	const [briefingOpen, setBriefingOpen] = useState(true);
	const [snoozeOpen, setSnoozeOpen] = useState(false);
	const panelRef = useRef(null);

	const alertCount = useMemo(() => {
		const items = Array.isArray(briefing?.items) ? briefing.items.length : 0;
		return items > 0 ? items : briefing ? 1 : 0;
	}, [briefing]);

	const needsYouCount = useMemo(() => {
		return challenges.filter((challenge) => getChallengePerspective(challenge)?.urgent).length;
	}, [challenges]);

	const hasBriefing = Boolean(briefing);
	const hasSnoozeNotice = Boolean(nudgesSnoozed && nudgeSnoozedUntil);
	const hasAlerts = hasBriefing || hasSnoozeNotice;
	const totalBadgeCount = alertCount + needsYouCount;

	useEffect(() => {
		if (!open) return undefined;

		function handleEscape(event) {
			if (event.key === 'Escape') setOpen(false);
		}

		document.addEventListener('keydown', handleEscape);
		return () => document.removeEventListener('keydown', handleEscape);
	}, [open]);

	useEffect(() => {
		if (hasBriefing && alertCount > 0) {
			setBriefingOpen(true);
		}
	}, [alertCount, hasBriefing]);

	useEffect(() => {
		if (challengeFocusNonce > 0) {
			setActiveTab('challenges');
			setOpen(true);
		}
	}, [challengeFocusNonce]);

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="fixed right-0 top-1/2 z-50 flex -translate-y-1/2 flex-col items-center rounded-l-xl border border-r-0 border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card px-2.5 py-3 shadow-lg transition hover:bg-gray-50 dark:hover:bg-dark-bg"
				aria-label={
					totalBadgeCount > 0 ? `Open trainer panel, ${totalBadgeCount} items` : 'Open trainer panel'
				}
				title="Trainer panel"
			>
				<i className="fa-solid fa-bell text-lg text-blue-600 dark:text-blue-400" aria-hidden="true" />
				{totalBadgeCount > 0 && (
					<span className="mt-1 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-semibold text-white">
						{totalBadgeCount}
					</span>
				)}
			</button>

			<div
				role="presentation"
				onClick={() => setOpen(false)}
				className={`fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
			/>

			<aside
				ref={panelRef}
				className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col border-l border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card shadow-2xl transform transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full pointer-events-none'}`}
				aria-hidden={!open}
			>
				<div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-dark-border px-4 py-4">
					<div>
						<p className={`text-base font-semibold ${aiTrainerHeadingClassName}`}>Trainer panel</p>
						<p className={`mt-0.5 text-xs ${aiTrainerMutedTextClassName}`}>
							Alerts and challenges that need you
						</p>
					</div>
					<button
						type="button"
						onClick={() => setOpen(false)}
						className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-300 dark:border-dark-border text-gray-600 dark:text-dark-text-secondary hover:bg-gray-50 dark:hover:bg-dark-bg transition"
						aria-label="Close trainer panel"
					>
						<i className="fa-solid fa-xmark" aria-hidden="true" />
					</button>
				</div>

				<div className="flex border-b border-gray-200 dark:border-dark-border">
					<RailTab
						active={activeTab === 'alerts'}
						count={alertCount}
						label="Alerts"
						onClick={() => setActiveTab('alerts')}
					/>
					<RailTab
						active={activeTab === 'challenges'}
						count={needsYouCount}
						label="Needs you"
						onClick={() => setActiveTab('challenges')}
					/>
				</div>

				<div className="flex-1 overflow-y-auto">
					{activeTab === 'alerts' && (
						<>
							{!hasAlerts && (
								<div className="px-4 py-8 text-center">
									<i
										className="fa-solid fa-bell-slash text-2xl text-gray-300 dark:text-dark-text-secondary"
										aria-hidden="true"
									/>
									<p className={`mt-3 text-sm ${aiTrainerMutedTextClassName}`}>
										No trainer alerts right now. Your coach will surface priorities here when something
										needs your attention.
									</p>
								</div>
							)}

							{hasSnoozeNotice && (
								<AccordionSection
									title="Reminders paused"
									count={0}
									open={snoozeOpen}
									onToggle={() => setSnoozeOpen((value) => !value)}
								>
									<p className={`text-sm ${aiTrainerMutedTextClassName}`}>
										Nudges are snoozed until{' '}
										{new Date(nudgeSnoozedUntil).toLocaleString(undefined, {
											month: 'short',
											day: 'numeric',
											hour: 'numeric',
											minute: '2-digit'
										})}
										.
									</p>
								</AccordionSection>
							)}

							{hasBriefing && (
								<AccordionSection
									title={briefing.headline || 'Coach briefing'}
									count={alertCount}
									open={briefingOpen}
									onToggle={() => setBriefingOpen((value) => !value)}
								>
									<AiTrainerBriefingBanner
										briefing={briefing}
										onDismiss={onDismissBriefing}
										onSnooze={onSnooze}
										onRunAction={onRunAction}
										actionLoading={actionLoading}
										variant="rail"
									/>
								</AccordionSection>
							)}
						</>
					)}

					{activeTab === 'challenges' && (
						<AiTrainerChallengesRailPanel
							challenges={challenges}
							loading={challengesLoading}
							error={challengesError}
							onRunAction={onRunAction}
							onDayCheck={onDayCheck}
							isDayCheckPending={isDayCheckPending}
							actionLoading={actionLoading}
						/>
					)}
				</div>
			</aside>
		</>
	);
}
